/**
 * Phase N — /accessibility · Accessibility Statement (draft, pending legal review).
 *
 * TODO: confirm WCAG conformance level after formal audit.
 */
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import LegalReviewBanner from "@/components/LegalReviewBanner";

const LAST_UPDATED = "February 2026";

function LegalSection({ title, id, testId, children }) {
  return (
    <section id={id} data-testid={testId} className="mt-10">
      <h2 className="font-serif text-2xl text-ivy tracking-tight">{title}</h2>
      <div className="mt-4 text-ink-soft leading-relaxed space-y-3 text-[15px]">{children}</div>
    </section>
  );
}

export default function Accessibility() {
  return (
    <Layout>
      <SEO
        title="Accessibility Statement | Beyond Valencia"
        description="Beyond Valencia is committed to accessibility. This statement describes the standards we aim for and how to report an accessibility issue."
        canonical="/accessibility"
      />
      <LegalReviewBanner />

      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16 sm:py-20" data-testid="accessibility-page">
        <div className="text-[10px] uppercase tracking-[0.22em] text-brass font-semibold">Legal · Draft</div>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl text-ivy tracking-tight" data-testid="accessibility-h1">
          Accessibility Statement
        </h1>
        <p className="mt-3 text-sm text-stone">Last updated: {LAST_UPDATED}</p>

        <p className="mt-8 text-ink-soft leading-relaxed">
          Beyond Valencia is committed to making our platform usable by the widest possible audience, including
          students with disabilities. Accessibility is a first-class product concern, not an afterthought.
        </p>

        <LegalSection title="1. Standard we aim for" id="standard" testId="accessibility-standard">
          <p>
            {/* TODO: confirm WCAG conformance level after formal audit */}
            We design and develop against the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA where
            reasonably practicable. Our tooling, component library, and content templates are structured to make
            AA the default state rather than something we retrofit.
          </p>
        </LegalSection>

        <LegalSection title="2. What we do" id="what-we-do" testId="accessibility-what-we-do">
          <ul className="list-disc pl-5 space-y-2">
            <li>Semantic HTML for all headings, forms, tables, and navigation.</li>
            <li>Keyboard operability for every interactive element.</li>
            <li>Sufficient color contrast on body text, buttons, and focus rings.</li>
            <li>Alt text on informative images. Decorative imagery is exposed as decorative.</li>
            <li>Descriptive link text — we do not ship &ldquo;click here&rdquo; anchors.</li>
            <li>Skip-to-content affordances on marketing routes.</li>
          </ul>
        </LegalSection>

        <LegalSection title="3. Known gaps" id="gaps" testId="accessibility-gaps">
          <p>
            {/* TODO: refresh list after next audit pass */}
            We publish a running list of known accessibility gaps so that users can decide whether a workaround is
            available. When a gap is fixed, it is removed from this list.
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Some third-party embeds (payments, analytics) inherit contrast ratios we do not control.</li>
            <li>PDF transcripts uploaded by students are not re-tagged; screen readers may not read them well.</li>
            <li>A comprehensive lazy-loading and preload pass on legacy marketing imagery is still in progress.</li>
          </ul>
        </LegalSection>

        <LegalSection title="4. Assistive technology" id="tech" testId="accessibility-tech">
          <p>
            We test against modern versions of Chrome, Firefox, Safari, and Edge on desktop and iOS/Android on
            mobile. Screen-reader testing is performed with VoiceOver and NVDA. If you encounter a barrier with a
            specific assistive technology, we want to hear about it.
          </p>
        </LegalSection>

        <LegalSection title="5. How to report an issue" id="report" testId="accessibility-report">
          <p>
            If you experience an accessibility barrier — a page that will not read out, a control you cannot
            operate with a keyboard, a color you cannot perceive — please tell us at{" "}
            {/* TODO: confirm mailbox routing */}
            <a href="mailto:hello@beyondvalencia.com" className="text-ivy hover:text-brass underline" data-testid="accessibility-contact-email">
              hello@beyondvalencia.com
            </a>
            . Describe the page and the barrier. We will acknowledge your report within five (5) business days and
            share a remediation timeline where possible.
          </p>
        </LegalSection>

        <LegalSection title="6. Contact" id="contact" testId="accessibility-contact">
          <p>
            {/* TODO: confirm exact legal entity name and mailing address before publishing */}
            Beyond Valencia LLC · Orlando, Florida ·{" "}
            <a href="mailto:hello@beyondvalencia.com" className="text-ivy hover:text-brass underline">
              hello@beyondvalencia.com
            </a>
          </p>
        </LegalSection>

        <p className="mt-16 text-xs text-stone italic" data-testid="accessibility-draft-disclaimer">
          This document is a draft pending legal review. It is not a substitute for advice from qualified counsel.
        </p>
      </div>
    </Layout>
  );
}
